import React, { useEffect, useMemo, useState } from 'react';
import styles from './AddFromMasterModal.module.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

function formatPreviewCell(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    const label = value.label;
    if (label != null && String(label).trim()) return String(label);
    return '—';
  }
  const text = String(value).trim();
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      text
    )
  ) {
    return '—';
  }
  return text;
}

function AddFromMasterModal({ template, getAuthHeaders, onClose, onAdded }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pageName, setPageName] = useState(template.name);
  const [referenceFields, setReferenceFields] = useState([]);
  const [refField, setRefField] = useState('');
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [summary, setSummary] = useState(null);
  const [previewLabels, setPreviewLabels] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [pageFields, setPageFields] = useState([]);
  const [masterName, setMasterName] = useState('');

  const selectedField = useMemo(
    () => referenceFields.find((field) => field.name === refField) || null,
    [referenceFields, refField]
  );

  const filterValues = useMemo(() => {
    if (!selectedField || !filterColumn) return [];
    const option = (selectedField.filter_options || []).find((item) => item.column === filterColumn);
    return option?.values || [];
  }, [selectedField, filterColumn]);

  useEffect(() => {
    let cancelled = false;
    const loadMeta = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/schemas/${template.id}/roster/meta`, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Failed to load master options');
        }
        if (cancelled) return;
        const fields = result.data?.reference_fields || [];
        setPageName(result.data?.page_name || template.name);
        setReferenceFields(fields);
        if (fields.length > 0) {
          setRefField(fields[0].name);
          setMasterName(fields[0].master_name || '');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load options');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [template.id, template.name, getAuthHeaders]);

  useEffect(() => {
    if (!refField || loading) return;
    let cancelled = false;

    const runPreview = async () => {
      setBusy(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/schemas/${template.id}/roster/preview`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ref_field: refField,
            filter:
              filterColumn && filterValue
                ? { column: filterColumn, value: filterValue }
                : null,
          }),
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Failed to preview');
        }
        if (cancelled) return;
        setSummary(result.summary);
        setPreviewLabels(result.preview_labels || []);
        setPreviewRows(result.preview_rows || []);
        setPageFields(result.page_fields || []);
        setMasterName(result.master_name || selectedField?.master_name || '');
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to preview');
          setSummary(null);
          setPreviewLabels([]);
          setPreviewRows([]);
          setPageFields([]);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    };

    runPreview();
    return () => {
      cancelled = true;
    };
  }, [
    refField,
    filterColumn,
    filterValue,
    template.id,
    getAuthHeaders,
    loading,
    selectedField?.master_name,
  ]);

  const close = () => {
    if (busy) return;
    onClose?.();
  };

  const handleRefFieldChange = (name) => {
    setRefField(name);
    setFilterColumn('');
    setFilterValue('');
    const field = referenceFields.find((item) => item.name === name);
    setMasterName(field?.master_name || '');
  };

  const confirmAdd = async () => {
    if (!refField || !(summary?.new_rows > 0)) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/schemas/${template.id}/roster/confirm`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ref_field: refField,
          filter:
            filterColumn && filterValue
              ? { column: filterColumn, value: filterValue }
              : null,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to add rows');
      }
      onAdded?.(result);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to add rows');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={close}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h3>
              Add {(masterName || 'records').toLowerCase()} to {pageName}
            </h3>
            <p className={styles.sub}>
              Pull rows from a linked master table, optionally filtered, then fill in the rest of the
              data.
            </p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={close} disabled={busy}>
            ×
          </button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <p className={styles.hint}>Loading…</p>
          ) : (
            <>
              {referenceFields.length > 1 && (
                <div className={styles.fieldBlock}>
                  <label className={styles.label}>Which field</label>
                  <select
                    className={styles.select}
                    value={refField}
                    onChange={(e) => handleRefFieldChange(e.target.value)}
                    disabled={busy}
                  >
                    {referenceFields.map((field) => (
                      <option key={field.name} value={field.name}>
                        {field.name} ← {field.master_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={styles.fieldBlock}>
                <label className={styles.label}>Filter by</label>
                <div className={styles.filterRow}>
                  <select
                    className={styles.select}
                    value={filterColumn}
                    onChange={(e) => {
                      setFilterColumn(e.target.value);
                      setFilterValue('');
                    }}
                    disabled={busy || !selectedField}
                  >
                    <option value="">Any</option>
                    {(selectedField?.filter_options || []).map((option) => (
                      <option key={option.column} value={option.column}>
                        {option.column}
                      </option>
                    ))}
                  </select>
                  <span className={styles.equals}>equals</span>
                  <select
                    className={styles.select}
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    disabled={busy || !filterColumn}
                  >
                    <option value="">Any</option>
                    {filterValues.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <p className={styles.summary}>
                {!summary
                  ? ''
                  : summary.matched === 0
                    ? `No matching records in ${masterName || 'master table'}.`
                    : `${summary.matched} match${summary.matched === 1 ? '' : 'es'} · ${
                        summary.new_rows
                      } not yet on this page · ${summary.already_added} already added.`}
              </p>

              {previewRows.length > 0 ? (
                <div className={styles.previewScroll}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        {pageFields.map((field) => (
                          <th key={field}>{field}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, idx) => (
                        <tr key={idx}>
                          {pageFields.map((field) => (
                            <td key={field}>
                              {formatPreviewCell(row[field])}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {(summary?.new_rows || 0) > previewRows.length && (
                        <tr>
                          <td colSpan={pageFields.length} className={styles.more}>
                            + {summary.new_rows - previewRows.length} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                previewLabels.length > 0 && (
                  <ul className={styles.previewList}>
                    {previewLabels.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                    {(summary?.new_rows || 0) > previewLabels.length && (
                      <li className={styles.more}>
                        + {summary.new_rows - previewLabels.length} more
                      </li>
                    )}
                  </ul>
                )
              )}
            </>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.btn} onClick={close} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnAccent}`}
            onClick={confirmAdd}
            disabled={busy || loading || !(summary?.new_rows > 0)}
          >
            {busy ? 'Working…' : 'Add rows'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddFromMasterModal;
